import React, { useState } from 'react';
import styled from 'styled-components';
import { withAuth, AuthState } from 'context/auth';
import { Input, Textarea, Button, UnstyledButton } from 'components';
import { slideUp } from 'styles/animations';
import { postErrorReport } from 'utils/api';
import { UserProfile } from 'context/auth/context';

const StyledContainer = styled.div<{ visible: boolean }>`
  width: 600px;
  position: absolute;
  top: 12px;
  right: ${p => (p.visible ? '12px' : '-600px')};
  opacity: ${p => (p.visible ? 1 : 0)};
  background-color: ${p => p.theme.colors.white};
  border: 1px solid ${p => p.theme.colors.black};
  z-index: ${p => (p.visible ? '100' : '-1')};
  visibility: ${p => (p.visible ? 'visible' : 'hidden')};
  transition:
    all 0.2s ease,
    opacity 0.1s ease;
  box-shadow:
    2px 2px 8px rgba(0, 0, 0, 0.18),
    0 1px 3px rgba(0, 0, 0, 0.12);
  -webkit-backface-visibility: hidden;
  border-radius: 8px;
  padding: 24px;
`;
const StyledFormHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 24px;
`;
const StyledTitle = styled.h3`
  margin: 0;
`;
const StyledCloseButton = styled.button`
  appearance: none;
  background-color: ${p => p.theme.colors.lightGrey};
  border: 1px solid ${p => p.theme.colors.actionFg};
  color: ${p => p.theme.colors.actionFg};
  transition: all 0.2s;
  margin-left: auto;
  width: 26px;
  height: 26px;
  font-size: ${p => p.theme.fontSize['2xl']};
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  line-height: 1;

  &:hover,
  &:focus {
    background-color: ${p => p.theme.colors.grey};
    color: ${p => p.theme.colors.actionFg};
  }
`;
const StyledButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;
const StyledSubmitButton = styled(Button)`
  color: ${p => p.theme.colors.actionFg};
  background-color: transparent;
  border: 2px solid ${p => p.theme.colors.actionFg};

  &:hover,
  &:active,
  &:focus {
    color: ${p => p.theme.colors.surface};
    background-color: ${p => p.theme.colors.actionFg};
    border-color: ${p => p.theme.colors.actionFg};
  }

  &:disabled {
    color: ${p => p.theme.colors.midGrey};
    background-color: transparent;
    border-color: ${p => p.theme.colors.midGrey};
  }
`;
const StyledResetButton = styled(UnstyledButton)`
  display: inline-block;
  text-decoration: none;
  outline: none;
  transition: background-color 0.2s ease;
  color: ${p => p.theme.colors.actionFg};
  border: 1px solid ${p => p.theme.colors.grey};
  background-color: ${p => p.theme.colors.white};
  padding: 12px 18px;
  border-radius: 20px;
  min-width: 140px;
  font-weight: bold;
  font-size: ${p => p.theme.fontSize.sm};
  text-align: center;
  margin-right: 12px;

  :hover:not([disabled]) {
    background-color: ${p => p.theme.colors.grey};
  }
  &[disabled] {
    cursor: default;
    color: ${p => p.theme.colors.grey};
  }
`;
const StyledAlertBox = styled.div<{ alertType?: string }>`
  ${slideUp}
  padding: 12px 12px 12px 24px;
  border-radius: 2px;
  margin: 20px auto 0 auto;
  border: 1px solid transparent;
  width: 100%;
  min-height: 70px;
  display: flex;
  align-items: center;

  color: ${p => (p.alertType === 'success' ? '#155724' : '#721c24')};
  background-color: ${p => (p.alertType === 'success' ? '#d4edda' : '#f8d7da')};
  border-color: ${p => (p.alertType === 'success' ? '#c3e6cb' : '#f5c6cb')};
`;
const StyledNote = styled.p`
  margin: 0;
  font-size: ${p => p.theme.fontSize.sm};
  margin-bottom: 40px;
`;
const StyledCharacterCount = styled.p`
  font-size: ${p => p.theme.fontSize.xs};
  text-align: right;
  margin: 4px 0 20px;
`;

interface Props {
  visible: boolean;
  onClose(): void;
  auth: AuthState;
  profile: UserProfile;
}

const _ErrorReportingForm = (props: Props) => {
  const [textareaValue, setTextareaValue] = useState('');
  const [submittingForm, setSubmittingForm] = useState(false);
  const [formError, setFormError] = useState<string | undefined>();
  const [submitSuccess, setSubmitSuccess] = useState<boolean | undefined>();

  const handleOnChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setTextareaValue(event.target.value);
    setFormError(undefined);
  };

  const closeForm = () => {
    props.onClose();
  };

  const submitForm = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!textareaValue) {
      setFormError('Please fill the required fields!');
      return false;
    }

    setSubmittingForm(true);

    try {
      const report = {
        error_description: textareaValue,
      };
      await postErrorReport(props.auth, report);
      setFormError(undefined);
      setSubmitSuccess(true);
      setTextareaValue('');
      // Messages show on error reporting form, gives time to read before closing automatically
      setTimeout(() => {
        setSubmitSuccess(undefined);
        closeForm();
      }, 3000);
    } catch (err) {
      setFormError('Error when sending report. Please try again later.');
    } finally {
      setSubmittingForm(false);
    }
  };

  return (
    <StyledContainer visible={props.visible}>
      <StyledFormHeader>
        <StyledTitle>Report an error</StyledTitle>
        <StyledCloseButton onClick={() => closeForm()}>
          &times;
        </StyledCloseButton>
      </StyledFormHeader>

      <form onSubmit={event => submitForm(event)}>
        <Input
          disabled
          name="name"
          type="text"
          labelText="Name"
          value={props.profile.name}
        />
        <Input
          disabled
          name="username"
          type="text"
          labelText="Username"
          value={props.profile.email}
        />
        <Textarea
          name="error_description"
          rows="10"
          labelText="Please describe your problem and what you were doing"
          required
          value={textareaValue}
          onChange={handleOnChange}
          disabled={submittingForm}
          maxLength={2000}
        />
        <StyledCharacterCount>
          {textareaValue.length}/2000 characters
        </StyledCharacterCount>
        <StyledNote>
          NOTE: We will collect required timestamps and error mesages from last
          24h and attach them automatically to report.
        </StyledNote>
        <StyledButtonRow>
          <StyledResetButton
            disabled={!textareaValue || submittingForm}
            onClick={() => {
              setTextareaValue('');
              setFormError(undefined);
              setSubmitSuccess(undefined);
            }}
          >
            Clear form
          </StyledResetButton>
          <StyledSubmitButton type="submit" disabled={submittingForm}>
            {submittingForm ? 'Submitting...' : 'Submit'}
          </StyledSubmitButton>
        </StyledButtonRow>
      </form>
      {formError !== undefined && (
        <StyledAlertBox alertType="danger">{formError}</StyledAlertBox>
      )}
      {submitSuccess === true && (
        <StyledAlertBox alertType="success">
          Report has been sent succesfully!
        </StyledAlertBox>
      )}
    </StyledContainer>
  );
};

const ErrorReportingForm = withAuth(_ErrorReportingForm);

export { ErrorReportingForm };
